'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '../../libs/utils';

import { BaseTopNavigationProps } from './type/BaseTopNavigationType';

export function BaseTopNavigation({
  leftAction,
  rightAction,
  className,
  title,
  bg = false,
  bgColor,
  titleAction,
  fullWidth = false,
  ...props
}: BaseTopNavigationProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [titleMaxWidthPx, setTitleMaxWidthPx] = useState<number | null>(null);

  const updateTitleMaxWidth = useCallback(() => {
    const row = rowRef.current;
    const left = leftRef.current;
    const right = rightRef.current;
    if (!row || !left || !right) return;

    const rowRect = row.getBoundingClientRect();
    const leftRect = left.getBoundingClientRect();
    const rightRect = right.getBoundingClientRect();

    const centerX = rowRect.left + rowRect.width / 2;
    const maxHalfLeft = centerX - leftRect.right;
    const maxHalfRight = rightRect.left - centerX;
    const half = Math.min(maxHalfLeft, maxHalfRight);
    const next = Math.max(0, Math.floor(2 * half));

    setTitleMaxWidthPx(next);
  }, []);

  useLayoutEffect(() => {
    if (!title) return;

    const row = rowRef.current;
    const left = leftRef.current;
    const right = rightRef.current;
    if (!row || !left || !right) return;

    updateTitleMaxWidth();

    const ro = new ResizeObserver(() => {
      updateTitleMaxWidth();
    });
    ro.observe(row);
    ro.observe(left);
    ro.observe(right);

    return () => {
      ro.disconnect();
    };
  }, [title, updateTitleMaxWidth]);

  return (
    <div
      className={cn(
        'z-top-navigation flex h-top-navigation items-center justify-center w-full',
        {
          'bg-neutral-background-main': bg
        },
        className
      )}
      style={{ backgroundColor: bgColor ? bgColor : '' }}
      {...props}
    >
      {title ? (
        <div
          ref={rowRef}
          className={cn('relative flex h-full w-full min-w-0 items-center justify-between overflow-x-hidden container-padding-small', {
            'container-xlarge': !fullWidth
          })}
        >
          <div ref={leftRef} className="relative z-10 flex shrink-0 justify-start">
            {leftAction}
          </div>
          <div
            data-testid="topNavigationTitle"
            className="pointer-events-none absolute left-1/2 top-1/2 z-[1] flex min-w-0 -translate-x-1/2 -translate-y-1/2 justify-center"
            style={{
              maxWidth: titleMaxWidthPx != null ? `${titleMaxWidthPx}px` : 'min(100% - 7rem, calc(100vw - 7rem))'
            }}
          >
            <div
              className={cn('body-large-semibold pointer-events-auto min-w-0 w-full max-w-full truncate text-center text-neutral-foreground-main', {
                'cursor-pointer': titleAction
              })}
              onClick={() => titleAction?.()}
            >
              {title}
            </div>
          </div>
          <div ref={rightRef} className="relative z-10 flex shrink-0 justify-end">
            {rightAction}
          </div>
        </div>
      ) : (
        <div
          className={cn('flex h-full w-full min-w-0 items-center justify-between overflow-x-hidden container-padding-small', {
            'container-xlarge': !fullWidth
          })}
        >
          <div className="flex shrink-0 justify-start">{leftAction}</div>
          <div className="flex shrink-0 justify-end">{rightAction}</div>
        </div>
      )}
    </div>
  );
}
